import { type ReactNode, useId } from "react"

import { cn } from "@/lib/utils"

// The one text-field skin for Night Shift forms (Requests, Start monitoring,
// and any form to come): a quiet bordered well on the control-height ladder,
// body type, the border lifting on hover and turning cyan on focus. The
// sign-in screen is the same skin at the "form" size.
const terminalFieldVariants = {
  l: "h-8 px-2.5",
  form: "h-12 px-3.5 text-[16px] leading-6",
} as const

export const terminalFieldClassName = (
  size: keyof typeof terminalFieldVariants = "l",
  isInvalid = false
) =>
  cn(
    "type-row-label border-border bg-card text-foreground placeholder:type-placeholder w-full rounded-sm border shadow-none transition-colors duration-(--duration-fast) ease-(--ease-terminal)",
    "hover:border-border-strong focus-visible:border-terminal-accent focus-visible:ring-terminal-accent focus-visible:shadow-(--terminal-glow) focus-visible:ring-1 focus-visible:outline-none",
    "disabled:cursor-not-allowed disabled:opacity-50",
    terminalFieldVariants[size],
    isInvalid && "border-signal-bad hover:border-signal-bad"
  )

// A labelled field: the label in the micro role above the control, the error
// beneath in the signal colour. The child renders the control with the id
// and invalid flag it needs.
export const Field = ({
  label,
  error,
  className,
  children,
}: {
  label: string
  error?: string
  className?: string
  children: (props: { id: string; isInvalid: boolean }) => ReactNode
}) => {
  const id = useId()
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-(--terminal-space-field)",
        className
      )}
    >
      <label htmlFor={id} className="type-micro text-muted-foreground">
        {label}
      </label>
      {children({ id, isInvalid: Boolean(error) })}
      {error && (
        <p role="alert" className="type-prose-sm text-signal-bad">
          {error}
        </p>
      )}
    </div>
  )
}
